import { useCallback, useDeferredValue, useEffect, useMemo, useRef, startTransition, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Tabs, TabsList } from "@nexus/ui";
import { Loader2, Trash2, XCircle } from "lucide-react";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import {
  useIamUsers,
  useIamTenants,
  useTenantPicker,
  useAccessGroupsForTenants,
  useAllUsersAccess,
  useAccessTemplates,
  useReactivateUser,
  useGrantAccess,
  useReplaceAccess,
  useBulkRevokeAccess,
  useGrantAccessTemplate,
  useApplyTemplateToOrg,
  useRemoveTemplateFromOrg,
  useIamInvites,
  useResendInvite,
  useRegenerateInvite,
  useInviteLink,
  useRevokeInvite,
  useDeleteAccessGroup,
} from "@/features/identity-hub/hooks/useIdentityHub";
import {
  flattenInfinitePages,
  mapAccessGroupToRoleRow,
  toIamTenantsListFilters,
  toIamUsersListFilters,
  toInvitesListFilters,
} from "@/features/identity-hub/lib/iam-list-filters";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { useIamDashboardStore, type IamDashTab } from "@/features/identity-hub/store/identity-hub-dashboard.store";
import { planTenantRoleAssignment } from "@/features/identity-hub/lib/tenant-role-assignment";
import { useGrantablePermissionTupleKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import {
  actingTenantIdForCatalogGrant,
  catalogTemplateId,
  isFixedCatalogAccess,
  isPerTenantAssignable,
  mergeAssignableCatalogRoles,
  peopleRoleFilterOptions,
  settlePeopleRoleFilter,
  settleSelectedPeopleRole,
  userHoldsFullFixedRole,
  userHoldsRoleOnTenants,
  type PeopleRoleFilterSnapshot,
} from "@/features/identity-hub/lib/access-scope";
import { accessIdFromTemplateGrantStatus } from "@/features/identity-hub/lib/invite-role-options";
import { IamKpiStrip } from "./IamKpiStrip";
import { PeopleTable, type PeopleFilters, type SortKey, type SortState } from "./PeopleTable";
import { PeopleToolbar } from "./PeopleToolbar";
import { BulkGrantDialog } from "./BulkGrantDialog";
import { OrgToolbar, type OrgFilters } from "./OrgToolbar";
import { OrgDrawer, type OrgDrawerTab } from "./OrgDrawer";
import { OrgsTable, type OrgSortKey, type OrgSortState } from "./OrgsTable";
import { buildBrandColorFilterOptions, tenantBrandColorFilterKey } from "@/features/identity-hub/lib/brand-colors";
import { TemplatesPane } from "./TemplatesPane";
import { NewOrgDialog } from "./NewOrgDialog";
import { InvitePersonDialog } from "./InvitePersonDialog";
import { IamListError, IamListSkeleton } from "./IamListError";
import { SuspendPersonDialog } from "./SuspendPersonDialog";
import { AuditLogModal, type AuditLogModalProps } from "./AuditLogModal";
import { InvitesToolbar, type InvitesFilters } from "./InvitesToolbar";
import { InvitesTable, type InviteSortKey, type InviteSortState } from "./InvitesTable";
import { InviteDrawer, type InviteDrawerTab } from "./InviteDrawer";
import { PersonDrawer, type PersonDrawerTab } from "./PersonDrawer";
import { RoleDrawer } from "./RoleDrawer";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";
import type { AccessGroupItem, UserItem, TenantListItem, InviteListItem } from "@/features/identity-hub/api/identity-hub.api";
import { buildInviteUrl } from "@/features/auth/lib/invite";
import { iamTabBadge } from "@/features/identity-hub/lib/iam-tab-badge";

const TAB_OPTION_VALUES: IamDashTab[] = ["orgs", "templates", "people", "invites"];
const IAM_STATS_WINDOW = "7d" as const;

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function IdentityHubDashboard() {
  const { t } = useTranslation();
  const { activeTab, setActiveTab } = useIamDashboardStore();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const authTenantCount = useAuth((s) => s.tenants.length);
  const loggedInEmail = useAuth((s) => s.user?.email);
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const grantableKeys = useGrantablePermissionTupleKeys();
  const canNewOrg = perms.IDENTITY_HUB_CREATE_TENANT.isOn && isSuperTenant;
  const canInvite = perms.IDENTITY_HUB_GENERATE_INVITE.isOn || isSuperTenant;
  const canViewInvites = perms.IDENTITY_HUB_LIST_INVITES.isOn || isSuperTenant;
  const canViewAuditLog = perms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn || isSuperTenant;
  const canShowTemplatesTab = perms.IDENTITY_HUB_LIST_TEMPLATES.isOn || isSuperTenant;
  const canGrantTemplate = perms.IDENTITY_HUB_GRANT_TEMPLATE.isOn || isSuperTenant;
  // Per-action gates for the invite row menu — mirror the backend's
  // requirePermission routes so the "…" items match what the caller may do
  // (super-tenants bypass, matching the API's SUPER_TENANT bypass).
  const canCopyInviteLink   = perms.IDENTITY_HUB_GET_INVITE_LINK.isOn   || isSuperTenant;
  const canResendInvite     = perms.IDENTITY_HUB_RESEND_INVITE.isOn     || isSuperTenant;
  const canRegenerateInvite = perms.IDENTITY_HUB_REGENERATE_INVITE.isOn || isSuperTenant;
  const canRevokeInvite     = perms.IDENTITY_HUB_REVOKE_INVITE.isOn     || isSuperTenant;
  const canDeleteRole       = perms.IDENTITY_HUB_DELETE_ACCESS.isOn     || isSuperTenant;

  // ── People tab state ──────────────────────────────────────────────
  const defaultPeopleOrgId = isSuperTenant ? null : activeTenantId;
  const [peopleFilters, setPeopleFilters] = useState<PeopleFilters>({
    search: "", orgId: defaultPeopleOrgId, role: null, status: null, noRole: false, hasPendingInvite: false,
  });

  useEffect(() => {
    if (isSuperTenant) return;
    setPeopleFilters((prev) => ({ ...prev, orgId: activeTenantId }));
  }, [activeTenantId, isSuperTenant]);
  const [peopleSort, setPeopleSort] = useState<SortState>({ key: "name", dir: "asc" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const [pinnedDrawerUser, setPinnedDrawerUser] = useState<UserItem | null>(null);

  useEffect(() => {
    if (activeTab === "templates" && !canShowTemplatesTab) {
      setActiveTab("orgs");
    }
  }, [activeTab, canShowTemplatesTab, setActiveTab]);

  // ── Orgs tab state ────────────────────────────────────────────────
  const [orgFilters, setOrgFilters] = useState<OrgFilters>({ search: "", size: null, showArchived: false, excludedColors: [] });
  const [orgSort, setOrgSort] = useState<OrgSortState>({ key: "name", dir: "asc" });
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  /** A role opened from the org drawer's Roles tab; only shown while that same org is open. */
  const [openedRole, setOpenedRole] = useState<{ orgId: string; roleId: string } | null>(null);
  const activeRoleId = openedRole && openedRole.orgId === activeOrgId ? openedRole.roleId : null;
  const [orgDrawerTab, setOrgDrawerTab] = useState<OrgDrawerTab>("general");
  const [orgDrawerFocus, setOrgDrawerFocus] = useState(0);
  const [personDrawerTab, setPersonDrawerTab] = useState<PersonDrawerTab>("access");
  const [activeInviteId, setActiveInviteId] = useState<string | null>(null);
  const [inviteDrawerTab, setInviteDrawerTab] = useState<InviteDrawerTab>("overview");

  // ── Invites tab state ─────────────────────────────────────────────
  const [invitesFilters, setInvitesFilters] = useState<InvitesFilters>({
    search: "", status: "all", tenantId: null,
  });
  const [invitesSort, setInvitesSort] = useState<InviteSortState>({ key: "createdAt", dir: "desc" });
  const onInvitesSort = useCallback((key: InviteSortKey) => {
    setInvitesSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);
  const [revokingInvite, setRevokingInvite] = useState<InviteListItem | null>(null);
  const [deletingRole, setDeletingRole] = useState<AccessGroupItem | null>(null);
  const deleteAccessGroup = useDeleteAccessGroup();
  const onInvitesFilterChange = <K extends keyof InvitesFilters>(key: K, value: InvitesFilters[K]) => {
    setInvitesFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ── Data (server-paginated People / Orgs / Invites / Roles) ───────
  const deferredPeopleSearch = useDeferredValue(peopleFilters.search);
  const deferredOrgSearch = useDeferredValue(orgFilters.search);
  const deferredInviteSearch = useDeferredValue(invitesFilters.search);

  const peopleApiFilters = useMemo(
    () => toIamUsersListFilters(peopleFilters, peopleSort, deferredPeopleSearch),
    [peopleFilters, peopleSort, deferredPeopleSearch],
  );
  const tenantsApiFilters = useMemo(
    () => toIamTenantsListFilters(orgFilters, {
      includeArchived: isSuperTenant,
      searchOverride: deferredOrgSearch,
      sort: orgSort,
    }),
    [orgFilters, isSuperTenant, deferredOrgSearch, orgSort],
  );
  const orgsActiveCountFilters = useMemo(
    () => toIamTenantsListFilters(orgFilters, {
      includeArchived: false,
      searchOverride: deferredOrgSearch,
    }),
    [orgFilters, deferredOrgSearch],
  );
  const invitesApiFilters = useMemo(
    () => toInvitesListFilters(invitesFilters, deferredInviteSearch, invitesSort),
    [invitesFilters, deferredInviteSearch, invitesSort],
  );

  const peopleQuery = useIamUsers(peopleApiFilters);
  const tenantsQuery = useIamTenants(tenantsApiFilters);
  const tenantPickerQuery = useTenantPicker();
  const orgsActiveCountQuery = useIamTenants(orgsActiveCountFilters, {
    enabled: isSuperTenant,
  });
  const invitesQuery = useIamInvites(invitesApiFilters, { enabled: canViewInvites });
  const templatesQuery = useAccessTemplates({}, { enabled: canShowTemplatesTab || canGrantTemplate });

  const users = useMemo(
    () => flattenInfinitePages<UserItem>(peopleQuery.data?.pages),
    [peopleQuery.data],
  );
  const allTenants = useMemo(
    () => flattenInfinitePages<TenantListItem>(tenantsQuery.data?.pages),
    [tenantsQuery.data],
  );
  const allInvites = useMemo(
    () => flattenInfinitePages<InviteListItem>(invitesQuery.data?.pages),
    [invitesQuery.data],
  );

  // `status` and `sentBy` can't be ordered by the listing API, so sort the
  // loaded rows here by name. Server-supported keys arrive pre-sorted.
  const sortedInvites = useMemo(() => {
    if (invitesSort.key !== "status" && invitesSort.key !== "sentBy") return allInvites;
    const factor = invitesSort.dir === "asc" ? 1 : -1;
    const keyOf = (inv: InviteListItem) =>
      invitesSort.key === "status"
        ? inv.status
        : inv.createdByName ?? inv.createdByEmail ?? "System";
    return [...allInvites].sort(
      (a, b) => factor * keyOf(a).localeCompare(keyOf(b), undefined, { sensitivity: "base" }),
    );
  }, [allInvites, invitesSort]);

  const tenants = useMemo(() => allTenants.filter((t) => !t.deletedAt), [allTenants]);
  const filteredTenants = useMemo(
    () => (orgFilters.showArchived ? allTenants : tenants),
    [orgFilters.showArchived, allTenants, tenants],
  );

  // ── Color filter derived data ─────────────────────────────────────
  const availableColors = useMemo(
    () => buildBrandColorFilterOptions(filteredTenants),
    [filteredTenants],
  );

  const colorFilteredTenants = useMemo(() => {
    if (orgFilters.excludedColors.length === 0) return filteredTenants;
    const available = new Set(availableColors.map((c) => c.value));
    const excluded = new Set(orgFilters.excludedColors.filter((c) => available.has(c)));
    if (excluded.size === 0) return filteredTenants;
    return filteredTenants.filter((t) => {
      const key = tenantBrandColorFilterKey(t);
      if (!key) return true;
      return !excluded.has(key);
    });
  }, [filteredTenants, orgFilters.excludedColors, availableColors]);

  // Invite / add-org pickers hide tenants that have no loaded roles
  // (InviteOrgRoleList, PersonDrawer). Groups must cover every picker
  // tenant, not only the Organizations-tab page currently on screen.
  const pickerTenantIds = useMemo(
    () => (tenantPickerQuery.data?.items ?? []).filter((t) => !t.deletedAt).map((t) => t.id),
    [tenantPickerQuery.data],
  );
  const tenantIds = useMemo(
    () => (pickerTenantIds.length > 0 ? pickerTenantIds : allTenants.map((t) => t.id)),
    [pickerTenantIds, allTenants],
  );
  const {
    groupsByTenant,
    isLoading: accessGroupsLoading,
    hasFailedChunk: accessGroupsHaveFailedChunk,
  } = useAccessGroupsForTenants(tenantIds, { includeArchived: isSuperTenant });

  // Members load server-side by accessId, and only while a role drawer is open.
  const roleMembersQuery = useIamUsers(
    { accessId: activeRoleId ?? undefined },
    { enabled: !!activeRoleId },
  );
  const activeRoleMembers = useMemo(
    () => flattenInfinitePages<UserItem>(roleMembersQuery.data?.pages),
    [roleMembersQuery.data],
  );

  // Org drawer membership is the tenant-scoped users list. The People tab is
  // paginated, so building the drawer from it under-counts any org larger than
  // the loaded pages. totalCount matches the Organizations table memberCount.
  const orgDrawerOpen = activeTab === "orgs" && !!activeOrgId;
  const orgMembersQuery = useIamUsers(
    { tenantId: activeOrgId ?? undefined },
    { enabled: orgDrawerOpen },
  );
  const orgMembersHaveData = !!orgMembersQuery.data && !orgMembersQuery.isPlaceholderData;
  // A later page can fail without discarding rows. Any other error, including
  // a same-key refetch, must not leave those cached rows selectable.
  const orgMembersNextPageFailed =
    orgDrawerOpen && orgMembersHaveData && orgMembersQuery.isFetchNextPageError;
  const orgMembersAreCurrent = orgMembersHaveData && (!orgMembersQuery.isError || orgMembersNextPageFailed);
  const activeOrgMembers = useMemo(
    () => (orgMembersAreCurrent ? flattenInfinitePages<UserItem>(orgMembersQuery.data?.pages) : []),
    [orgMembersAreCurrent, orgMembersQuery.data],
  );
  const activeOrg = allTenants.find((tenant) => tenant.id === activeOrgId) ?? null;
  const activeOrgMemberCount = orgMembersAreCurrent
    ? orgMembersQuery.data?.pages[0]?.totalCount
    : activeOrg?.memberCount;
  // `isPending` covers the frame before the fetch starts. A failed first page
  // or a failed refetch is an error, not a spinner.
  const orgMembersInitialError = orgDrawerOpen && orgMembersQuery.isError && !orgMembersNextPageFailed;
  const orgMembersLoading =
    orgDrawerOpen &&
    !orgMembersInitialError &&
    !orgMembersAreCurrent &&
    (orgMembersQuery.isPending || orgMembersQuery.isFetching || orgMembersQuery.isPlaceholderData);
  const activeRoleGroup = useMemo(
    () => (activeOrgId && activeRoleId
      ? (groupsByTenant.get(activeOrgId) ?? []).find((group) => group.id === activeRoleId) ?? null
      : null),
    [activeOrgId, activeRoleId, groupsByTenant],
  );
  const activeRole = useMemo(
    () => (activeRoleGroup && activeOrg ? mapAccessGroupToRoleRow(activeRoleGroup, activeOrg) : null),
    [activeRoleGroup, activeOrg],
  );

  // Include open role/org members and the open person so their access resolves
  // even when they're outside the loaded People page. Jumping here from an org
  // resets people filters, which swaps that page.
  const userIds = useMemo(
    () => [...new Set([
      ...users.map((u) => u.id),
      ...activeRoleMembers.map((m) => m.id),
      ...activeOrgMembers.map((m) => m.id),
      ...(activeUserId ? [activeUserId] : []),
    ])],
    [users, activeRoleMembers, activeOrgMembers, activeUserId],
  );
  const { accessByUser, accessPhaseByUser, refetch: refetchUsersAccess } = useAllUsersAccess(userIds);

  const allAccessGroups = useMemo(
    () => [...groupsByTenant.values()].flat(),
    [groupsByTenant],
  );

  const activeUsers = useMemo(() => users.filter((u) => u.status === "ACTIVE"), [users]);
  const pendingUsers = useMemo(() => users.filter((u) => u.status === "PENDING"), [users]);
  const deactivatedUsers = useMemo(() => users.filter((u) => u.status === "DEACTIVATED"), [users]);

  const neverLoggedIn = useMemo(
    () => activeUsers.filter((u) => !u.lastSeenAt).length,
    [activeUsers],
  );

  const rolelessCount = useMemo(
    () => activeUsers.filter((u) => !(accessByUser.get(u.id)?.length)).length,
    [activeUsers, accessByUser],
  );

  const orgsAllTotal = tenantsQuery.data?.pages[0]?.totalCount;
  const orgsActiveTotal = isSuperTenant
    ? orgsActiveCountQuery.data?.pages[0]?.totalCount
    : orgsAllTotal;
  const archivedTenantCount = isSuperTenant && orgsAllTotal != null && orgsActiveTotal != null
    ? Math.max(orgsAllTotal - orgsActiveTotal, 0)
    : undefined;
  const orgsTotal = isSuperTenant && !orgFilters.showArchived
    ? orgsActiveTotal
    : orgsAllTotal;
  const peopleCount = peopleQuery.data
    ? (peopleQuery.data.pages[0]?.totalCount ?? users.length)
    : peopleQuery.isSuccess
      ? 0
      : undefined;
  const templatesCount = templatesQuery.data?.length;

  const inviteStatusCounts = useMemo(() => {
    const server = invitesQuery.data?.pages[0]?.statusCounts;
    if (server) {
      return {
        all: server.all ?? 0,
        pending: server.pending ?? 0,
        expired: server.expired ?? 0,
        accepted: server.accepted ?? 0,
      };
    }
    const counts = { all: allInvites.length, pending: 0, expired: 0, accepted: 0 };
    for (const inv of allInvites) counts[inv.status]++;
    return counts;
  }, [invitesQuery.data, allInvites]);
  const invitesCount = invitesQuery.data
    ? inviteStatusCounts.all
    : invitesQuery.isSuccess
      ? 0
      : undefined;
  const orgLogosByTenantId = useMemo(
    () => new Map(tenants.map((t) => [t.id, t.logoUrl ?? null])),
    [tenants],
  );

  // The auth-store tenant snapshot (powering the nav tenant switcher) only carries
  // logo URLs as of the last token refresh, so newly uploaded logos don't appear in
  // the switcher. The IAM org list is the freshest source — mirror its logo URLs back
  // into the auth store so the switcher matches the org cards.
  const syncAuthTenants = useAuth((s) => s.updateTenants);
  useEffect(() => {
    if (orgLogosByTenantId.size === 0) return;
    const authTenants = useAuth.getState().tenants;
    let changed = false;
    const next = authTenants.map((tenant) => {
      if (!orgLogosByTenantId.has(tenant.id)) return tenant;
      const freshLogoUrl = orgLogosByTenantId.get(tenant.id) ?? null;
      if ((tenant.logoUrl ?? null) === freshLogoUrl) return tenant;
      changed = true;
      return { ...tenant, logoUrl: freshLogoUrl };
    });
    if (changed) syncAuthTenants(next);
  }, [orgLogosByTenantId, syncAuthTenants]);
  const resendInvite = useResendInvite();
  const regenerateInviteMut = useRegenerateInvite();
  const inviteLinkMut = useInviteLink();
  const revokeInviteMut = useRevokeInvite();
  const busyInviteId = resendInvite.isPending
    ? resendInvite.variables?.id ?? null
    : inviteLinkMut.isPending
      ? inviteLinkMut.variables ?? null
      : regenerateInviteMut.isPending
        ? null
        : revokeInviteMut.isPending
          ? revokeInviteMut.variables ?? null
          : null;
  const handleResendInvite = (invite: InviteListItem) => {
    resendInvite.mutate({ id: invite.id });
  };
  const handleRegenerateInvite = (invite: InviteListItem) => {
    regenerateInviteMut.mutate({ email: invite.email });
  };
  // Write the invite link to the clipboard and confirm with a toast (or warn
  // if the clipboard write fails / is unavailable).
  const copyInviteLinkToClipboard = useCallback(async (token: string) => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard-unavailable");
      await navigator.clipboard.writeText(buildInviteUrl(token));
      toast.success(t("iam.toast.inviteLinkCopied", "Invite link copied to clipboard"));
    } catch {
      toast.error(t("iam.toast.inviteLinkCopyFailed", "Couldn’t copy to clipboard — please copy it manually."));
    }
  }, [t]);
  // Copy the EXISTING link (read-only) — must NOT regenerate, which would
  // expire the link already emailed to the invitee (AD-1714).
  const handleCopyInviteLink = (invite: InviteListItem) => {
    inviteLinkMut.mutate(invite.id, {
      onSuccess: (data) => { void copyInviteLinkToClipboard(data.token); },
    });
  };
  const handleConfirmRevoke = () => {
    if (!revokingInvite) return;
    revokeInviteMut.mutate(revokingInvite.id, {
      // Close on both paths: on success the invite is gone; on failure the
      // hook refreshes the list (e.g. it was already accepted), so the dialog
      // no longer applies.
      onSettled: () => setRevokingInvite(null),
    });
  };
  const handleConfirmDeleteRole = () => {
    if (!deletingRole) return;
    deleteAccessGroup.mutate(
      { accessId: deletingRole.id, tenantId: deletingRole.tenantId },
      { onSettled: () => setDeletingRole(null) },
    );
  };

  // ── Cross-tab navigation flash ────────────────────────────────────
  const [flashUserId, setFlashUserId] = useState<string | null>(null);
  const [flashOrgId, setFlashOrgId] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const orgsScrollRef = useRef<HTMLDivElement>(null);
  // Prevents the tab-change cleanup effect from clearing the drawer opened by a jump
  const isJumping = useRef(false);

  // Cross-tab jumps wrap the heavy bits (tab + filter reset, which mount the
  // target table) in startTransition so the click feels instant. The flash
  // markers and isJumping ref stay synchronous so the post-tab-change effect
  // and scroll-to-row logic still see them on the same render pass.
  const jumpToPerson = (userId: string, tab: PersonDrawerTab = "access") => {
    clearTimeout(flashTimer.current);
    isJumping.current = true;
    setActiveUserId(userId);
    setPersonDrawerTab(tab);
    setFlashUserId(userId);
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: false, hasPendingInvite: false });
    });
    flashTimer.current = setTimeout(() => setFlashUserId(null), 1500);
  };

  const jumpToOrg = (tenantId: string) => {
    clearTimeout(flashTimer.current);
    isJumping.current = true;
    setActiveUserId(null);
    setActiveOrgId(tenantId);
    setFlashOrgId(tenantId);
    startTransition(() => {
      setActiveTab("orgs");
      setOrgFilters({ search: "", size: null, showArchived: false, excludedColors: [] });
    });
    flashTimer.current = setTimeout(() => setFlashOrgId(null), 1500);
  };

  const goToTemplatesToCreate = useCallback(() => {
    setActiveTab("templates");
  }, [setActiveTab]);

  const [showNewOrgDialog, setShowNewOrgDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [auditLogState, setAuditLogState] = useState<{ open: boolean; highlightId: string | null; searchPrefill: string | null; category: AuditLogModalProps["category"] }>({ open: false, highlightId: null, searchPrefill: null, category: null });
  // Opening the audit modal mounts 100+ rows + Radix portal; wrapping in
  // startTransition lets the browser paint the button click first, then
  // renders the dialog content as an interruptible lower-priority update.
  //
  // `highlightId` scrolls to a specific entry (used by AuditTimeline rows in
  // drawers); `searchPrefill` pre-populates the search box so per-row "..."
  // menus land on a filtered view of that user/invite/role;
  // `category` pre-selects the action-category filter chip.
  const openAuditLog = useCallback(
    // `category` is widened to `string | null` so child components (PeopleTable,
    // InvitesTable, drawers) — which don't import the `ActionCategory` enum —
    // can wire this callback directly. The modal itself validates the value.
    (options: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null } = {}) => {
      startTransition(() =>
        setAuditLogState({
          open: true,
          highlightId:   options.highlightId   ?? null,
          searchPrefill: options.searchPrefill  ?? null,
          category:      (options.category ?? null) as AuditLogModalProps["category"],
        }),
      );
    },
    [],
  );
  // Closing is kept synchronous so Radix can start the exit animation
  // without delay (forceMount keeps rows in DOM so there's no unmount cost).
  const handleAuditLogChange = useCallback((open: boolean) => {
    setAuditLogState({ open, highlightId: null, searchPrefill: null, category: null });
  }, []);
  const [showBulkGrant, setShowBulkGrant] = useState(false);
  const reactivateUser = useReactivateUser();
  const grantAccess = useGrantAccess();
  const replaceAccess = useReplaceAccess();
  const bulkRevokeAccess = useBulkRevokeAccess();
  const grantTemplate = useGrantAccessTemplate();
  const applyTemplateToOrg = useApplyTemplateToOrg();
  const removeTemplateFromOrg = useRemoveTemplateFromOrg();
  const removingTemplateId = removeTemplateFromOrg.isPending
    ? removeTemplateFromOrg.variables?.templateId
    : undefined;
  const removingWildcardRoleId = removingTemplateId && activeOrgId
    ? (groupsByTenant.get(activeOrgId) ?? []).find(
        (group) => catalogTemplateId(group) === removingTemplateId,
      )?.id ?? null
    : null;
  const [suspendingUser, setSuspendingUser] = useState<UserItem | null>(null);
  const handleUnsuspend = (u: UserItem) => {
    if (!activeTenantId) return;
    reactivateUser.mutate({ userId: u.id, tenantId: activeTenantId });
  };

  const handleAddMemberToOrg = (userId: string, accessId: string) => {
    if (!activeOrgId) return;
    const group = (groupsByTenant.get(activeOrgId) ?? []).find((row) => row.id === accessId);
    if (group && isFixedCatalogAccess(group)) {
      const templateId = catalogTemplateId(group);
      if (!templateId) return;
      grantTemplate.mutate({ templateId, tenantId: activeOrgId, userIds: [userId] });
      return;
    }
    if (group && !isPerTenantAssignable(group)) return;
    const plan = planTenantRoleAssignment(
      userId,
      accessId,
      activeOrgId,
      accessByUser.get(userId),
    );
    if (plan.kind === "replace") {
      replaceAccess.mutate(plan);
      return;
    }
    if (plan.kind === "grant") {
      grantAccess.mutate({ userId, accessId, tenantId: activeOrgId });
    }
  };

  const handleAddMembersToOrg = (userIds: string[], accessId: string) => {
    for (const userId of userIds) {
      handleAddMemberToOrg(userId, accessId);
    }
  };

  const handleRemoveFromOrg = (userId: string, orgId: string) => {
    const entries = (accessByUser.get(userId) ?? []).filter(
      (e) => e.access.tenantId === orgId && isPerTenantAssignable(e.access),
    );
    if (entries.length === 0) return;
    bulkRevokeAccess.mutate({
      userId,
      revocations: entries.map((e) => ({ accessId: e.accessId, tenantId: orgId })),
      tenantId: orgId,
    });
  };

  const handleBulkRemoveFromOrg = (userIds: string[], orgId: string) => {
    for (const userId of userIds) {
      handleRemoveFromOrg(userId, orgId);
    }
  };

  const handleBulkChangeRoleOnOrg = (userIds: string[], accessId: string, orgId: string) => {
    const group = (groupsByTenant.get(orgId) ?? []).find((row) => row.id === accessId);
    if (!group) return;
    if (isFixedCatalogAccess(group)) {
      const templateId = catalogTemplateId(group);
      if (!templateId) return;
      grantTemplate.mutate({ templateId, tenantId: orgId, userIds });
      return;
    }
    if (!isPerTenantAssignable(group)) return;
    for (const userId of userIds) {
      const plan = planTenantRoleAssignment(userId, accessId, orgId, accessByUser.get(userId));
      if (plan.kind === "replace") {
        replaceAccess.mutate(plan);
      } else if (plan.kind === "grant") {
        grantAccess.mutate({ userId, accessId, tenantId: orgId });
      }
    }
  };

  const handleBulkAddRoleOnOrg = (userIds: string[], accessId: string, orgId: string) => {
    const group = (groupsByTenant.get(orgId) ?? []).find((row) => row.id === accessId);
    if (!group) return;
    const userIdsWithoutRole = userIds.filter(
      (userId) => !(accessByUser.get(userId) ?? []).some((entry) => entry.accessId === accessId),
    );
    if (userIdsWithoutRole.length === 0) return;
    if (isFixedCatalogAccess(group)) {
      const templateId = catalogTemplateId(group);
      if (!templateId) return;
      grantTemplate.mutate({ templateId, tenantId: orgId, userIds: userIdsWithoutRole });
      return;
    }
    if (!isPerTenantAssignable(group)) return;
    for (const userId of userIdsWithoutRole) {
      grantAccess.mutate({ userId, accessId, tenantId: orgId });
    }
  };

  const handleBulkResendInvites = (inviteIds: string[]) => {
    for (const id of inviteIds) {
      resendInvite.mutate({ id });
    }
  };

  const catalogRoles = useMemo(
    () => mergeAssignableCatalogRoles(templatesQuery.data ?? [], allAccessGroups),
    [templatesQuery.data, allAccessGroups],
  );

  const inviteWildcardTemplates = useMemo(
    () =>
      isSuperTenant
        ? catalogRoles.filter((row) => row.scopeMode === "WILDCARD" && row.status === "ACTIVE")
        : [],
    [isSuperTenant, catalogRoles],
  );

  const handleBulkGrant = (templateId: string, tenantIds?: string[]) => {
    const template = catalogRoles.find((row) => row.id === templateId);
    if (!template) return;
    const isWildcard = template.scopeMode === "WILDCARD";
    const tenantId = actingTenantIdForCatalogGrant({
      scopeMode: template.scopeMode,
      ownerTenantId: template.canBeEditedByTenantId,
      selectedTenantIds: tenantIds,
      activeTenantId,
    });
    if (!tenantId) return;
    if (isWildcard && (!tenantIds || tenantIds.length === 0)) return;
    const userIds = Array.from(selected).filter((userId) => {
      if (isWildcard) {
        return !userHoldsRoleOnTenants(accessByUser.get(userId), template.id, tenantIds ?? []);
      }
      return !userHoldsFullFixedRole(accessByUser.get(userId), template);
    });
    if (userIds.length === 0) return;
    grantTemplate.mutate(
      isWildcard
        ? { templateId, tenantId, userIds, tenantIds }
        : { templateId, tenantId, userIds },
      {
        onSuccess: () => {
          setShowBulkGrant(false);
          setSelected(new Set());
        },
      },
    );
  };

  const handleExportCsv = (scope: "selected" | "all" = "selected") => {
    const rowUsers = scope === "selected" && selected.size > 0
      ? users.filter((u) => selected.has(u.id))
      : users;

    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = [
      t("iam.csvExport.headers.name", "Name"),
      t("iam.csvExport.headers.email", "Email"),
      t("iam.csvExport.headers.status", "Status"),
      t("iam.csvExport.headers.organizations", "Organizations"),
      t("iam.csvExport.headers.primaryRole", "Primary Role"),
      t("iam.csvExport.headers.authProvider", "Auth Provider"),
      t("iam.csvExport.headers.joined", "Joined"),
      t("iam.csvExport.headers.lastActive", "Last Active"),
    ];
    const rows = rowUsers.map((u) => {
      const entries = accessByUser.get(u.id) ?? [];
      const orgs = [...new Set(entries.map((e) => e.access.tenant.name))].join("; ");
      const role = entries[0]?.access.name ?? "";
      return [
        u.displayName,
        u.email,
        u.status,
        orgs,
        role,
        u.authProvider ?? "",
        new Date(u.createdAt).toLocaleDateString(),
        u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleDateString() : "",
      ].map(escape).join(",");
    });

    const csv = [header.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `people-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onOrgFilterChange = <K extends keyof OrgFilters>(key: K, value: OrgFilters[K]) => {
    setOrgFilters((prev) => ({ ...prev, [key]: value }));
  };

  const superTenantIds = useMemo(
    () => new Set(tenants.filter((t) => t.type === "SUPER_TENANT").map((t) => t.id)),
    [tenants],
  );

  // People-page membership. The org drawer does not use this map — it only
  // backfills a table cell when the tenant row has no server memberCount.
  const membersByTenant = useMemo(() => {
    const map = new Map<string, typeof users>();
    for (const [userId, entries] of accessByUser) {
      const user = users.find((u) => u.id === userId);
      if (!user) continue;
      const seen = new Set<string>();
      for (const entry of entries) {
        const tid = entry.access.tenantId;
        if (seen.has(tid)) continue;
        seen.add(tid);
        if (!map.has(tid)) map.set(tid, []);
        map.get(tid)!.push(user);
      }
    }
    return map;
  }, [accessByUser, users]);

  const deletingRoleMemberCount = useMemo(() => {
    if (!deletingRole) return 0;
    let count = 0;
    for (const entries of accessByUser.values()) {
      if (entries.some((e) => e.accessId === deletingRole.id)) count += 1;
    }
    return count;
  }, [deletingRole, accessByUser]);

  // ── KPI insights — temporarily disabled alongside KPI strip ──────
  // const kpiInsights = useMemo(() => {
  //   // Largest org by member count.
  //   let topOrg: { name: string; members: number } | null = null;
  //   for (const [tid, members] of membersByTenant) {
  //     if (!topOrg || members.length > topOrg.members) {
  //       const tenant = tenants.find((t) => t.id === tid);
  //       if (tenant) topOrg = { name: tenant.name, members: members.length };
  //     }
  //   }
  //
  //   // Most-populated access role.
  //   let topRole: { name: string; members: number } | null = null;
  //   for (const [accessId, memberIds] of membersByGroup) {
  //     const size = memberIds.size;
  //     if (!topRole || size > topRole.members) {
  //       for (const groups of groupsByTenant.values()) {
  //         const g = groups.find((g) => g.id === accessId);
  //         if (g) { topRole = { name: g.name, members: size }; break; }
  //       }
  //     }
  //   }
  //
  //   // Cross-org users (multi-tenant accounts) — straightforward signal of
  //   // access sprawl; useful "fun" stat.
  //   let crossOrgUsers = 0;
  //   for (const entries of accessByUser.values()) {
  //     const tenantsForUser = new Set(entries.map((e) => e.access.tenantId));
  //     if (tenantsForUser.size > 1) crossOrgUsers++;
  //   }
  //
  //   // Newest account — show as relative time string.
  //   const newestAccount = users
  //     .filter((u) => u.createdAt)
  //     .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  //   const newestAccountAgo = newestAccount ? formatRelativeShort(newestAccount.createdAt) : null;
  //
  //   // Invite acceptance rate + avg time to accept (lifetime).
  //   let acceptanceRate: number | null = null;
  //   let avgAcceptDays: number | null = null;
  //   let invitesSent7d: number | null = null;
  //   if (allInvites.length > 0) {
  //     const accepted = allInvites.filter((i) => i.status === "accepted");
  //     acceptanceRate = Math.round((accepted.length / allInvites.length) * 100);
  //
  //     if (accepted.length > 0) {
  //       const totalAcceptMs = accepted.reduce((sum, i) => {
  //         if (!i.usedAt) return sum;
  //         return sum + (new Date(i.usedAt).getTime() - new Date(i.createdAt).getTime());
  //       }, 0);
  //       const avgDays = totalAcceptMs / accepted.length / 86_400_000;
  //       avgAcceptDays = avgDays >= 1 ? Math.round(avgDays) : Math.max(1, Math.round(avgDays * 10) / 10);
  //     }
  //
  //     const since7d = Date.now() - 7 * 86_400_000;
  //     invitesSent7d = allInvites.filter((i) => new Date(i.createdAt).getTime() >= since7d).length;
  //   }
  //
  //   return { topOrg, topRole, crossOrgUsers, newestAccountAgo, acceptanceRate, avgAcceptDays, invitesSent7d };
  // }, [membersByTenant, tenants, membersByGroup, groupsByTenant, accessByUser, users, allInvites]);

  // Reset selection + drawers when tab changes (skip when tab change is from a jump navigation)
  useEffect(() => {
    if (isJumping.current) {
      isJumping.current = false;
      return;
    }
    setSelected(new Set());
    setActiveUserId(null);
    setActiveOrgId(null);
    setActiveInviteId(null);
  }, [activeTab]);

  const onFilterChange = useCallback(<K extends keyof PeopleFilters>(key: K, value: PeopleFilters[K]) => {
    setPeopleFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSort = useCallback((key: SortKey) => {
    setPeopleSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);

  const onOrgSort = useCallback((key: OrgSortKey) => {
    setOrgSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }, []);

  const onToggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const onToggleAll = useCallback((ids: string[]) => {
    setSelected((prev) =>
      ids.every((id) => prev.has(id)) ? new Set() : new Set(ids),
    );
  }, []);

  // ── Stable PeopleTable row callbacks ──────────────────────────────
  // Pinned identity so the memoized `PersonRow` (React.memo) can skip
  // re-rendering rows on dashboard state changes that don't touch them.
  const onPersonRowClick = useCallback((id: string) => {
    setActiveUserId((prev) => (prev === id ? null : id));
    setPersonDrawerTab("access");
  }, []);
  const onPersonEdit = useCallback((u: UserItem) => {
    setActiveUserId(u.id);
    setPersonDrawerTab("profile");
  }, []);
  const onPersonSuspend = useCallback((u: UserItem) => setSuspendingUser(u), []);
  const onPersonUnsuspend = useCallback((u: UserItem) => {
    if (!activeTenantId) return;
    reactivateUser.mutate({ userId: u.id, tenantId: activeTenantId });
  }, [activeTenantId, reactivateUser]);
  // Copies the invite's EXISTING link via GET /invites/:id/link (read-only) —
  // it must not regenerate, which would expire the invitee's emailed link
  // (AD-1714). Only reachable when the person has a pending invite.
  const onPersonCopyInviteLink = useCallback((u: UserItem, _accessId?: string) => {
    const inviteId = u.pendingInvite?.inviteId;
    if (!inviteId) return;
    inviteLinkMut.mutate(inviteId, {
      onSuccess: (data) => { void copyInviteLinkToClipboard(data.token); },
    });
  }, [inviteLinkMut, copyInviteLinkToClipboard]);

  const peopleRoleScopeKey = peopleFilters.orgId ?? "";
  // A selected org can render as soon as its own groups arrive. "All orgs"
  // must wait for every chunk — the first chunk often contains a single role,
  // which is what made the dropdown flicker. A failed chunk is not a finished
  // load: the partial map would drop roles from that request and clear a
  // selection that is still valid.
  const scopedOrgLoaded = peopleFilters.orgId != null && groupsByTenant.has(peopleFilters.orgId);
  const grantKeysReady = isSuperTenant || authTenantCount > 0;
  const peopleRoleFilterComplete =
    grantKeysReady &&
    accessGroupsHaveFailedChunk !== true &&
    (scopedOrgLoaded || accessGroupsLoading !== true);
  const nextPeopleRoleFilter = useMemo(
    () => ({
      scopeKey: peopleRoleScopeKey,
      roles: peopleRoleFilterOptions(groupsByTenant, grantableKeys, peopleFilters.orgId, {
        includeFixedCatalog: canGrantTemplate,
      }),
      isComplete: peopleRoleFilterComplete,
    }),
    [
      groupsByTenant,
      grantableKeys,
      peopleFilters.orgId,
      peopleRoleScopeKey,
      peopleRoleFilterComplete,
      canGrantTemplate,
    ],
  );
  const [settledPeopleRoleFilter, setSettledPeopleRoleFilter] = useState<PeopleRoleFilterSnapshot>({
    scopeKey: "",
    roles: [],
  });
  const resolvedPeopleRoleFilter = settlePeopleRoleFilter(settledPeopleRoleFilter, nextPeopleRoleFilter);
  if (resolvedPeopleRoleFilter !== settledPeopleRoleFilter) {
    setSettledPeopleRoleFilter(resolvedPeopleRoleFilter);
  }
  const nextSelectedPeopleRole = settleSelectedPeopleRole(peopleFilters.role, {
    roles: resolvedPeopleRoleFilter.roles,
    isComplete: peopleRoleFilterComplete,
  });
  if (nextSelectedPeopleRole !== peopleFilters.role) {
    setPeopleFilters({ ...peopleFilters, role: nextSelectedPeopleRole });
  }
  const availableRoles = resolvedPeopleRoleFilter.roles;

  // ── Tab options ───────────────────────────────────────────────────
  const tabOptions = useMemo(
    () => {
      const failedLabel = (tabKey: (typeof TAB_OPTION_VALUES)[number]) =>
        t("iam.dashboard.loadError.tabFailed", {
          tab: t(`iam.dashboard.tabs.${tabKey}`),
          defaultValue: "Failed to load {{tab}}",
        });
      return TAB_OPTION_VALUES
        .filter((value) => value !== "templates" || canShowTemplatesTab)
        .map((value) => ({
          value,
          label: t(`iam.dashboard.tabs.${value}`),
          badge:
            value === "people"
              ? iamTabBadge(peopleQuery.isError, peopleCount, failedLabel("people"))
              : value === "templates"
                ? iamTabBadge(templatesQuery.isError, templatesCount, failedLabel("templates"))
                : value === "orgs"
                  ? iamTabBadge(tenantsQuery.isError, orgsTotal, failedLabel("orgs"))
                  : value === "invites"
                    ? iamTabBadge(invitesQuery.isError, invitesCount, failedLabel("invites"))
                    : undefined,
        }));
    },
    [
      t,
      canShowTemplatesTab,
      peopleQuery.isError,
      peopleCount,
      templatesQuery.isError,
      templatesCount,
      tenantsQuery.isError,
      orgsTotal,
      invitesQuery.isError,
      invitesCount,
    ],
  );

  // ── Scroll containers for virtualized tables ─────────────────────
  // Each tab's table virtualizes its rows against the scroller of that
  // tab's content area, so only ~15 visible rows mount instead of all N.
  const peopleScrollRef = useRef<HTMLDivElement>(null);
  const invitesScrollRef = useRef<HTMLDivElement>(null);
  const peopleSentinelRef = useRef<HTMLDivElement>(null);
  const orgsSentinelRef = useRef<HTMLDivElement>(null);
  const invitesSentinelRef = useRef<HTMLDivElement>(null);

  useInfiniteScroll(
    peopleSentinelRef,
    peopleScrollRef,
    () => { void peopleQuery.fetchNextPage(); },
    !!peopleQuery.hasNextPage,
    peopleQuery.isFetchingNextPage,
    { enabled: activeTab === "people", recheckKey: users.length },
  );
  useInfiniteScroll(
    orgsSentinelRef,
    orgsScrollRef,
    () => { void tenantsQuery.fetchNextPage(); },
    !!tenantsQuery.hasNextPage,
    tenantsQuery.isFetchingNextPage,
    { enabled: activeTab === "orgs", recheckKey: `${allTenants.length}:${filteredTenants.length}` },
  );
  useInfiniteScroll(
    invitesSentinelRef,
    invitesScrollRef,
    () => { void invitesQuery.fetchNextPage(); },
    !!invitesQuery.hasNextPage,
    invitesQuery.isFetchingNextPage,
    { enabled: activeTab === "invites" && canViewInvites, recheckKey: allInvites.length },
  );

  // ── Stable toolbar/dialog inputs ──────────────────────────────────
  // Without these `useMemo`s, `tenants.map(...)` was rebuilt every render
  // and handed to PeopleToolbar / RolesToolbar / InvitesToolbar —
  // invalidating their entire prop trees on every click anywhere on the dashboard.
  const orgsForToolbar = useMemo(
    () => tenants.map((t) => ({
      id: t.id,
      name: t.name,
      type: t.type,
      logoUrl: t.logoUrl,
      brandColor: t.brandColor,
    })),
    [tenants],
  );
  // Invite / role / grant / person-drawer pickers must not reuse the Orgs-tab
  // infinite list: that query is the first page (or the current search), so a
  // tenant like Vodafone is missing until someone filters for it on Organizations.
  // Do not fall back to `tenants` while the picker is loading — that truncated
  // list is the bug (AD-2335).
  const orgsForPickers = useMemo(() => {
    const source = (tenantPickerQuery.data?.items ?? []).filter((t) => !t.deletedAt);
    return source.map((t) => ({
      id: t.id,
      name: t.name,
      type: t.type,
      logoUrl: t.logoUrl,
      brandColor: t.brandColor,
    }));
  }, [tenantPickerQuery.data]);

  // ── KPI strip click handlers ──────────────────────────────────────
  // Stable identities so the memoized `IamKpiStrip` (and its 8 memoized
  // KpiCards) can skip re-renders on every dashboard state change.
  // Switching tabs synchronously mounts a new table (people / orgs / roles /
  // invites) with up to hundreds of rows, so each setActiveTab call is
  // wrapped in startTransition — React paints the click feedback first and
  // renders the new tab as an interruptible low-priority update.
  const onKpiPeopleClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: false, hasPendingInvite: false });
    });
  }, [setActiveTab]);
  const onKpiOrgsClick = useCallback(() => {
    startTransition(() => setActiveTab("orgs"));
  }, [setActiveTab]);
  const onKpiPendingClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("invites");
      setInvitesFilters({ search: "", status: "pending", tenantId: null });
    });
  }, [setActiveTab]);
  const onKpiRolesClick = useCallback(() => {
    startTransition(() => setActiveTab("templates"));
  }, [setActiveTab]);
  const onKpiDeactivatedClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: "DEACTIVATED", noRole: false, hasPendingInvite: false });
    });
  }, [setActiveTab]);
  const onKpiRolelessClick = useCallback(() => {
    startTransition(() => {
      setActiveTab("people");
      setPeopleFilters({ search: "", orgId: null, role: null, status: null, noRole: true, hasPendingInvite: false });
    });
  }, [setActiveTab]);

  const listedDrawerUser = activeUserId
    ? users.find((u) => u.id === activeUserId) ?? activeRoleMembers.find((u) => u.id === activeUserId) ?? null
    : null;
  useEffect(() => {
    if (!activeUserId) {
      setPinnedDrawerUser((current) => (current === null ? current : null));
      return;
    }
    if (!listedDrawerUser) return;
    setPinnedDrawerUser((current) => (current === listedDrawerUser ? current : listedDrawerUser));
  }, [activeUserId, listedDrawerUser]);
  const drawerUser = listedDrawerUser
    ?? (pinnedDrawerUser?.id === activeUserId ? pinnedDrawerUser : null);
  const drawerAccessPhase = activeUserId ? accessPhaseByUser.get(activeUserId) ?? "loading" : "ready";

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">
      {/* KPI strip — temporarily hidden; preserved for Storybook / future re-enable */}
      {/* <IamKpiStrip
        accountsTotal={stats?.people.current ?? users.length}
        orgsTotal={stats?.orgs.current ?? tenants.length}
        orgsInactive={stats?.orgs.totalInactive ?? 0}
        privilegedTotal={stats?.privileged.current ?? 0}
        pendingTotal={stats?.pending.current ?? inviteStatusCounts.pending}
        peopleDelta={stats?.people.delta ?? 0}
        orgsDelta={stats?.orgs.delta ?? 0}
        privilegedDelta={stats?.privileged.delta ?? 0}
        pendingDelta={stats?.pending.delta ?? 0}
        adminGroupCount={adminGroupCount}
        invitedCount={pendingUsers.length}
        activeCount={activeUsers.length}
        oldestPendingDays={stats?.pending.oldestDays ?? null}
        rolesTotal={totalGroups}
        emptyRolesCount={emptyRolesCount}
        deactivatedTotal={deactivatedUsers.length}
        neverLoggedIn={neverLoggedIn}
        rolelessCount={rolelessCount}
        statsWindow={IAM_STATS_WINDOW}
        loading={loading || statsLoading}
        insights={kpiInsights}
        onPeopleClick={onKpiPeopleClick}
        onOrgsClick={onKpiOrgsClick}
        onPendingClick={onKpiPendingClick}
        onRolesClick={onKpiRolesClick}
        onDeactivatedClick={onKpiDeactivatedClick}
        onRolelessClick={onKpiRolelessClick}
      /> */}

      {/* Tab bar + Audit log on one row */}
      <div className="flex items-center gap-2 px-3 pt-3 sm:px-6 sm:pt-6">
        <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
          <Tabs value={activeTab} onValueChange={(v) => startTransition(() => setActiveTab(v as IamDashTab))}>
            <TabsList
              variant="line"
              options={tabOptions}
              className="h-11 min-w-max rounded-none border-b-0 bg-transparent p-0"
            />
          </Tabs>
        </div>
        {canViewAuditLog && (
          <Button variant="outline" size="sm" className="shrink-0" iconStart="clock" onClick={() => openAuditLog()}>
            {t("iam.dashboard.auditLog")}
          </Button>
        )}
      </div>

      {/* Content area */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">

          {/* People tab */}
          {activeTab === "people" && (
            <>
              <PeopleToolbar
                filters={peopleFilters}
                onFilterChange={onFilterChange}
                selectedCount={selected.size}
                onClearSelection={() => setSelected(new Set())}
                onInvite={() => setShowInviteDialog(true)}
                onBulkAssignRole={() => setShowBulkGrant(true)}
                onBulkExport={() => handleExportCsv("selected")}
                orgs={orgsForToolbar}
                roles={availableRoles}
                canInvite={canInvite}
                canAssignFixedRole={canGrantTemplate}
              />
              <div ref={peopleScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {peopleQuery.isError ? (
                  <IamListError
                    error={peopleQuery.error}
                    onRetry={() => { void peopleQuery.refetch(); }}
                  />
                ) : peopleQuery.isLoading ? (
                  <IamListSkeleton />
                ) : (
                  <>
                    <PeopleTable
                      users={users}
                      accessByUser={accessByUser}
                      sort={peopleSort}
                      onSort={onSort}
                      selected={selected}
                      onToggleRow={onToggleRow}
                      onToggleAll={onToggleAll}
                      activeId={activeUserId}
                      onRowClick={onPersonRowClick}
                      onEditProfile={onPersonEdit}
                      onSuspend={onPersonSuspend}
                      onUnsuspend={onPersonUnsuspend}
                      onOpenAuditLog={canViewAuditLog ? openAuditLog : undefined}
                      onCopyInviteLink={canCopyInviteLink ? onPersonCopyInviteLink : undefined}
                      flashId={flashUserId}
                      scrollContainerRef={peopleScrollRef}
                      hasMore={peopleQuery.hasNextPage}
                      isFetchingNextPage={peopleQuery.isFetchingNextPage}
                    />
                    <div ref={peopleSentinelRef} className="h-px" aria-hidden />
                    {peopleQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-3 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Orgs tab */}
          {activeTab === "orgs" && (
            <>
              <OrgToolbar
                filters={orgFilters}
                onFilterChange={onOrgFilterChange}
                availableColors={availableColors}
                onNewOrg={() => setShowNewOrgDialog(true)}
                canCreate={canNewOrg}
                archivedCount={archivedTenantCount}
              />
              <div ref={orgsScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {tenantsQuery.isError ? (
                  <IamListError
                    error={tenantsQuery.error}
                    onRetry={() => { void tenantsQuery.refetch(); }}
                  />
                ) : tenantsQuery.isLoading ? (
                  <IamListSkeleton />
                ) : (
                  <OrgsTable
                    tenants={colorFilteredTenants}
                    membersByTenant={membersByTenant}
                    groupsByTenant={groupsByTenant}
                    superTenantIds={superTenantIds}
                    sort={orgSort}
                    onSort={onOrgSort}
                    activeId={activeOrgId}
                    onRowClick={(id) => {
                      setOrgDrawerTab("general");
                      setOrgDrawerFocus((n) => n + 1);
                      setActiveOrgId((prev) => (prev === id ? null : id));
                    }}
                    onOpenSettings={(id) => {
                      setOrgDrawerTab("general");
                      setOrgDrawerFocus((n) => n + 1);
                      setActiveOrgId(id);
                    }}
                    onOpenAuditLog={
                      canViewAuditLog ? (_id, name) => openAuditLog({ searchPrefill: name }) : undefined
                    }
                    flashId={flashOrgId}
                    scrollContainerRef={orgsScrollRef}
                    hasMore={tenantsQuery.hasNextPage}
                    isFetchingNextPage={tenantsQuery.isFetchingNextPage}
                  />
                )}
                {!tenantsQuery.isError && !tenantsQuery.isLoading && (
                  <>
                    <div ref={orgsSentinelRef} className="h-px" aria-hidden />
                    {tenantsQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-3 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Templates tab — omitted from the tab bar unless LIST_TEMPLATES is granted or the caller is a super-tenant */}
          {activeTab === "templates" && canShowTemplatesTab && (
            <TemplatesPane orgs={orgsForPickers} activeTenantId={activeTenantId} />
          )}

          {/* Invites tab */}
          {activeTab === "invites" && (
            <>
              <InvitesToolbar
                filters={invitesFilters}
                onFilterChange={onInvitesFilterChange}
                orgs={orgsForToolbar}
                onInvite={() => setShowInviteDialog(true)}
                canInvite={canInvite}
                counts={inviteStatusCounts}
              />
              <div ref={invitesScrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {invitesQuery.isError ? (
                  <IamListError
                    error={invitesQuery.error}
                    onRetry={() => { void invitesQuery.refetch(); }}
                  />
                ) : invitesQuery.isLoading ? (
                  <IamListSkeleton />
                ) : (
                  <>
                    <InvitesTable
                      invites={sortedInvites}
                      loading={invitesQuery.isFetching && allInvites.length === 0}
                      orgLogosByTenantId={orgLogosByTenantId}
                      onCopyLink={canCopyInviteLink ? handleCopyInviteLink : undefined}
                      onResend={canResendInvite ? handleResendInvite : undefined}
                      onRegenerate={canRegenerateInvite ? handleRegenerateInvite : undefined}
                      onRevoke={canRevokeInvite ? (invite) => setRevokingInvite(invite) : undefined}
                      onViewAudit={canViewAuditLog ? (invite) => {
                        setActiveInviteId(invite.id);
                        setInviteDrawerTab("audit");
                      } : undefined}
                      activeId={activeInviteId}
                      onRowClick={(id) => {
                        setInviteDrawerTab("overview");
                        setActiveInviteId((prev) => (prev === id ? null : id));
                      }}
                      busyInviteId={busyInviteId}
                      sort={invitesSort}
                      onSort={onInvitesSort}
                      scrollContainerRef={invitesScrollRef}
                    />
                    <div ref={invitesSentinelRef} className="h-px" aria-hidden />
                    {invitesQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-3 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <PersonDrawer
        open={activeTab === "people" && !!activeUserId}
        initialTab={personDrawerTab}
        user={drawerUser}
        access={activeUserId ? (accessByUser.get(activeUserId) ?? []) : []}
        isAccessLoading={drawerAccessPhase === "loading"}
        isAccessError={drawerAccessPhase === "error"}
        onRetryAccess={() => { void refetchUsersAccess(); }}
        allTenants={orgsForPickers}
        accessGroupsByTenant={groupsByTenant}
        onClose={() => setActiveUserId(null)}
        activeTenantId={activeTenantId}
        loggedInEmail={loggedInEmail}
        onSuspend={() => {
          if (drawerUser) setSuspendingUser(drawerUser);
        }}
        onUnsuspend={() => {
          if (drawerUser) handleUnsuspend(drawerUser);
        }}
        onJumpToOrg={jumpToOrg}
        onCreateRole={canShowTemplatesTab ? goToTemplatesToCreate : undefined}
        onOpenAuditLog={openAuditLog}
        wildcardTemplates={inviteWildcardTemplates}
        wildcardTemplatesLoading={isSuperTenant && templatesQuery.isFetching}
      />

      <OrgDrawer
        open={activeTab === "orgs" && !!activeOrgId && !activeRole}
        initialTab={orgDrawerTab}
        tabFocusToken={orgDrawerFocus}
        org={activeOrg}
        members={activeOrgMembers}
        memberCount={activeOrgMemberCount}
        membersLoading={orgMembersLoading}
        membersError={orgMembersInitialError ? orgMembersQuery.error : undefined}
        onRetryMembers={() => { void orgMembersQuery.refetch(); }}
        hasMoreMembers={orgMembersAreCurrent && orgMembersQuery.hasNextPage}
        isFetchingMoreMembers={orgMembersQuery.isFetchingNextPage}
        membersNextPageFailed={orgMembersNextPageFailed}
        onLoadMoreMembers={() => { void orgMembersQuery.fetchNextPage(); }}
        roles={activeOrgId ? (groupsByTenant.get(activeOrgId) ?? []).filter((r) => !r.deletedAt) : []}
        accessByUser={accessByUser}
        allUsers={users}
        isSuperTenant={!!activeOrgId && superTenantIds.has(activeOrgId)}
        onClose={() => setActiveOrgId(null)}
        onJumpToPerson={(userId) => {
          setActiveOrgId(null);
          jumpToPerson(userId);
        }}
        onRemoveFromOrg={(userId) => { if (activeOrgId) handleRemoveFromOrg(userId, activeOrgId); }}
        onJumpToRole={(roleId) => { if (activeOrgId) setOpenedRole({ orgId: activeOrgId, roleId }); }}
        onBulkRemoveFromOrg={(userIds) => { if (activeOrgId) handleBulkRemoveFromOrg(userIds, activeOrgId); }}
        onBulkChangeRole={(userIds, accessId) => {
          if (activeOrgId) handleBulkChangeRoleOnOrg(userIds, accessId, activeOrgId);
        }}
        onBulkAddRole={(userIds, accessId) => {
          if (activeOrgId) handleBulkAddRoleOnOrg(userIds, accessId, activeOrgId);
        }}
        onBulkResendInvite={handleBulkResendInvites}
        isBulkMembersBusy={
          grantAccess.isPending ||
          replaceAccess.isPending ||
          grantTemplate.isPending ||
          bulkRevokeAccess.isPending ||
          resendInvite.isPending
        }
        onRemoveRole={canDeleteRole ? (roleId) => {
          const role = activeOrgId ? groupsByTenant.get(activeOrgId)?.find((r) => r.id === roleId) : undefined;
          if (role) setDeletingRole(role);
        } : undefined}
        onEditProfile={(u) => jumpToPerson(u.id, "profile")}
        onSuspend={(u) => setSuspendingUser(u)}
        onUnsuspend={(u) => handleUnsuspend(u)}
        wildcardTemplates={inviteWildcardTemplates}
        onGrantWildcardTemplate={async (userIds, templateId) => {
          if (!activeOrgId) return null;
          const status = await grantTemplate.mutateAsync({ templateId, tenantId: activeOrgId, userIds });
          return accessIdFromTemplateGrantStatus(status, activeOrgId);
        }}
        onApplyWildcardTemplate={(templateId) => {
          if (!activeOrgId) return;
          applyTemplateToOrg.mutate({ templateId, tenantId: activeOrgId });
        }}
        onRemoveWildcardTemplate={(roleId) => {
          if (!activeOrgId) return;
          const role = groupsByTenant.get(activeOrgId)?.find((group) => group.id === roleId);
          const templateId = role ? catalogTemplateId(role) : null;
          if (!templateId) return;
          removeTemplateFromOrg.mutate({ templateId, tenantId: activeOrgId });
        }}
        isApplyingWildcardRole={applyTemplateToOrg.isPending}
        isRemovingWildcardRole={removeTemplateFromOrg.isPending}
        removingWildcardRoleId={removingWildcardRoleId}
        onAddMembers={handleAddMembersToOrg}
        isAddingMember={grantAccess.isPending || replaceAccess.isPending || grantTemplate.isPending}
        isRemovingMember={bulkRevokeAccess.isPending}
        removingMemberId={bulkRevokeAccess.isPending ? bulkRevokeAccess.variables?.userId ?? null : null}
        onCreateRole={canShowTemplatesTab ? goToTemplatesToCreate : undefined}
        onOpenAuditLog={openAuditLog}
      />

      {activeTab === "orgs" && activeRole && (
        <RoleDrawer
          role={activeRole}
          initialTab="permissions"
          members={activeRoleMembers}
          membersLoading={roleMembersQuery.isLoading}
          hasMoreMembers={roleMembersQuery.hasNextPage}
          isFetchingMoreMembers={roleMembersQuery.isFetchingNextPage}
          onLoadMoreMembers={() => { void roleMembersQuery.fetchNextPage(); }}
          permissions={activeRoleGroup?.permissions ?? []}
          allUsers={users}
          accessByUser={accessByUser}
          onClose={() => setOpenedRole(null)}
          onJumpToPerson={(userId) => {
            setOpenedRole(null);
            setActiveOrgId(null);
            jumpToPerson(userId);
          }}
          onOpenAuditLog={openAuditLog}
        />
      )}

      <InviteDrawer
        open={activeTab === "invites" && !!activeInviteId}
        initialTab={inviteDrawerTab}
        invite={sortedInvites.find((i) => i.id === activeInviteId) ?? null}
        onClose={() => setActiveInviteId(null)}
        onCopyLink={canCopyInviteLink ? handleCopyInviteLink : undefined}
        onResend={canResendInvite ? handleResendInvite : undefined}
        onRegenerate={canRegenerateInvite ? handleRegenerateInvite : undefined}
        onRevoke={canRevokeInvite ? (invite) => setRevokingInvite(invite) : undefined}
        busy={!!activeInviteId && busyInviteId === activeInviteId}
      />

      {/* New org dialog */}
      <NewOrgDialog
        open={showNewOrgDialog}
        onOpenChange={setShowNewOrgDialog}
        superTenantId={activeTenantId}
        onInviteFirstAdmin={() => setShowInviteDialog(true)}
      />

      {/* Invite person dialog */}
      <InvitePersonDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        orgs={orgsForPickers}
        accessGroups={allAccessGroups}
        wildcardTemplates={inviteWildcardTemplates}
      />

      {/* Audit log modal */}
      <AuditLogModal
        open={auditLogState.open}
        onOpenChange={handleAuditLogChange}
        highlightId={auditLogState.highlightId}
        searchPrefill={auditLogState.searchPrefill}
        category={auditLogState.category}
      />

      {/* Bulk grant dialog */}
      <BulkGrantDialog
        open={showBulkGrant}
        onOpenChange={setShowBulkGrant}
        selectedUserIds={Array.from(selected)}
        accessByUser={accessByUser}
        templates={catalogRoles}
        orgs={orgsForPickers}
        onApply={handleBulkGrant}
        isPending={grantTemplate.isPending}
      />

      {/* Suspend account dialog */}
      <SuspendPersonDialog
        user={suspendingUser}
        open={!!suspendingUser}
        onOpenChange={(v) => { if (!v) setSuspendingUser(null); }}
        activeTenantId={activeTenantId}
      />

      {/* Revoke invite confirmation dialog */}
      <Dialog
        open={!!revokingInvite}
        onOpenChange={(v) => { if (!v) setRevokingInvite(null); }}
      >
        <DialogContent className="max-w-sm" {...PREVENT_DISMISS_PROPS}>
          <DialogHeader>
            <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10">
              <XCircle className="h-4 w-4 text-destructive" />
            </div>
            <DialogTitle>{t("iam.dashboard.invites.revokeDialog.title", "Revoke invite?")}</DialogTitle>
            <DialogDescription>
              {revokingInvite && (
                <>
                  {t("iam.dashboard.invites.revokeDialog.body", "The invite link sent to ")}
                  <strong className="text-foreground">{revokingInvite.email}</strong>{" "}
                  {t("iam.dashboard.invites.revokeDialog.bodyTail", "will stop working immediately.")}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setRevokingInvite(null)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={revokeInviteMut.isPending}
              onClick={handleConfirmRevoke}
            >
              {revokeInviteMut.isPending
                ? t("iam.dashboard.invites.revokeDialog.pending", "Revoking…")
                : t("iam.dashboard.invites.revokeDialog.confirm", "Revoke invite")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete role confirmation dialog */}
      <Dialog
        open={!!deletingRole}
        onOpenChange={(v) => { if (!v) setDeletingRole(null); }}
      >
        <DialogContent className="max-w-sm" {...PREVENT_DISMISS_PROPS}>
          <DialogHeader>
            <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <DialogTitle>{t("iam.dashboard.roles.deleteDialog.title", "Delete role?")}</DialogTitle>
            <DialogDescription>
              {deletingRole && (
                <>
                  <strong className="text-foreground">{deletingRole.name}</strong>
                  {t("iam.dashboard.roles.deleteDialog.bodyMiddle", " in ")}
                  <strong className="text-foreground">
                    {allTenants.find((tenant) => tenant.id === deletingRole.tenantId)?.name}
                  </strong>
                  {t("iam.dashboard.roles.deleteDialog.bodySuffix", " will be permanently removed.")}
                  {deletingRoleMemberCount > 0 && (
                    <span className="mt-2 block text-destructive">
                      {t("iam.dashboard.roles.deleteDialog.memberWarning", {
                        count: deletingRoleMemberCount,
                        defaultValue_one: "{{count}} member will immediately lose access.",
                        defaultValue_other: "{{count}} members will immediately lose access.",
                      })}
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeletingRole(null)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteAccessGroup.isPending}
              onClick={handleConfirmDeleteRole}
            >
              {deleteAccessGroup.isPending
                ? t("iam.dashboard.roles.deleteDialog.pending", "Deleting…")
                : t("iam.dashboard.roles.deleteDialog.confirm", "Delete role")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
